import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchAdminDoctors,
  fetchAdminReviewers,
  fetchAdminSessions,
  fetchCentrals,
  fetchConsents,
  fetchDoctors,
  fetchReviewers,
  fetchSessions,
} from "@/lib/queries";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { cn } from "@/lib/cn";
import { useIsAdminPortal } from "@/lib/portal";

export const OPEN_SEARCH_EVENT = "sij:open-search";

const RECENTS_KEY = "sij_recent_searches";
const MAX_RECENTS = 6;

type EntityType = "doctor" | "reviewer" | "session" | "consent" | "central";

interface SearchResult {
  type: EntityType;
  id: number;
  title: string;
  subtitle?: string;
  to: string;
}

function getRecents(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function pushRecent(q: string) {
  const next = [q, ...getRecents().filter((r) => r !== q)].slice(0, MAX_RECENTS);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
}

const typeIcon: Record<EntityType, React.ReactNode> = {
  doctor: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2v6a4 4 0 0 0 8 0V2" />
      <circle cx="18" cy="14" r="2.5" />
      <path d="M10 12v3a6 6 0 0 0 5.5 6" />
    </svg>
  ),
  reviewer: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 21v-1a6 6 0 0 1 12 0v1" />
    </svg>
  ),
  session: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="12" height="18" rx="2" />
      <path d="M9 4V2h6v2M9 12h6M9 16h4" />
    </svg>
  ),
  consent: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5M9 13h6M9 17h4" />
    </svg>
  ),
  central: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 4 5v7c0 5 3.5 9 8 10 4.5-1 8-5 8-10V5z" />
    </svg>
  ),
};

async function searchCentralPortal(q: string): Promise<SearchResult[]> {
  const [doctors, reviewers, sessions, consents] = await Promise.allSettled([
    fetchDoctors({ search: q, per_page: 5 }),
    fetchReviewers({ search: q, per_page: 5 }),
    fetchSessions({ search: q, per_page: 5 }),
    fetchConsents({ search: q, per_page: 5 }),
  ]);
  const out: SearchResult[] = [];
  if (doctors.status === "fulfilled") {
    out.push(
      ...doctors.value.data.map((d): SearchResult => ({ type: "doctor", id: d.id, title: d.name, subtitle: d.email, to: `/doctors/${d.id}` })),
    );
  }
  if (reviewers.status === "fulfilled") {
    out.push(
      ...reviewers.value.data.map((r): SearchResult => ({ type: "reviewer", id: r.id, title: r.name, subtitle: r.mobile, to: `/reviewers/${r.id}` })),
    );
  }
  if (sessions.status === "fulfilled") {
    out.push(
      ...sessions.value.data.map((s): SearchResult => ({ type: "session", id: s.id, title: s.title ?? s.code, subtitle: s.patient_name, to: `/sessions/${s.id}` })),
    );
  }
  if (consents.status === "fulfilled") {
    out.push(
      ...consents.value.data
        .filter((c) => `${c.title} ${c.patient_name}`.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 5)
        .map((c): SearchResult => ({ type: "consent", id: c.id, title: c.title, subtitle: c.patient_name, to: "/contracts" })),
    );
  }
  return out;
}

async function searchAdminPortal(q: string): Promise<SearchResult[]> {
  const [centrals, doctors, reviewers, sessions] = await Promise.allSettled([
    fetchCentrals({ search: q, per_page: 5 }),
    fetchAdminDoctors({ search: q, per_page: 5 }),
    fetchAdminReviewers({ search: q, per_page: 5 }),
    fetchAdminSessions({ search: q, per_page: 5 }),
  ]);
  const out: SearchResult[] = [];
  if (centrals.status === "fulfilled") {
    out.push(
      ...centrals.value.data.map((c): SearchResult => ({ type: "central", id: c.id, title: c.name, subtitle: c.license_number ?? undefined, to: `/admin/centrals/${c.id}` })),
    );
  }
  if (doctors.status === "fulfilled") {
    out.push(
      ...doctors.value.data.map((d): SearchResult => ({ type: "doctor", id: d.id, title: d.name, subtitle: d.email, to: `/admin/doctors/${d.id}` })),
    );
  }
  if (reviewers.status === "fulfilled") {
    out.push(
      ...reviewers.value.data.map((r): SearchResult => ({ type: "reviewer", id: r.id, title: r.name, subtitle: r.mobile ?? undefined, to: `/admin/reviewers/${r.id}` })),
    );
  }
  if (sessions.status === "fulfilled") {
    out.push(
      ...sessions.value.data.map((s): SearchResult => ({ type: "session", id: s.id, title: s.title ?? `#${s.id}`, subtitle: s.reviewer?.name, to: `/admin/sessions/${s.id}` })),
    );
  }
  return out;
}

/** Global command-palette search (roadmap 2.1) — Cmd+K / Ctrl+K. */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Portal from shared context, not an ad-hoc pathname check. [audit P0-3b]
  const adminPortal = useIsAdminPortal();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebouncedValue(query, 300);

  const openPalette = useCallback(() => {
    setOpen(true);
    setQuery("");
    setResults([]);
    setActiveIndex(0);
    setRecents(getRecents());
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openPalette();
      }
    };
    const onOpenEvent = () => openPalette();
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpenEvent);
    };
  }, [openPalette]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = debounced.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (adminPortal ? searchAdminPortal(q) : searchCentralPortal(q))
      .then((res) => {
        if (!cancelled) {
          setResults(res);
          setActiveIndex(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, open, adminPortal]);

  const grouped = useMemo(() => {
    const order: EntityType[] = ["central", "doctor", "reviewer", "session", "consent"];
    return order
      .map((type) => ({ type, items: results.filter((r) => r.type === type) }))
      .filter((g) => g.items.length > 0);
  }, [results]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  function select(result: SearchResult) {
    if (query.trim()) pushRecent(query.trim());
    setOpen(false);
    navigate(result.to);
  }

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flat[activeIndex]) {
      e.preventDefault();
      select(flat[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  if (!open) return null;

  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label={t("globalSearch.title")}>
      <div className="absolute inset-0 bg-navy/55 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden />
      <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-card shadow-lift" data-testid="global-search">
        <div className="flex items-center gap-3 border-b border-line px-4">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-ink-3">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t("globalSearch.placeholder")}
            className="h-12 w-full bg-transparent text-[14px] text-ink-1 outline-none placeholder:text-ink-3"
            data-testid="global-search-input"
          />
          <kbd className="shrink-0 rounded border border-line px-1.5 py-0.5 text-[10px] font-semibold text-ink-3">Esc</kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {loading && <div className="px-3 py-6 text-center text-[12.5px] text-ink-3">{t("common.loading")}</div>}

          {!loading && query.trim().length < 2 && (
            <div>
              {recents.length > 0 ? (
                <>
                  <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-ink-3">
                    {t("globalSearch.recent")}
                  </div>
                  {recents.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setQuery(r)}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-[13px] text-ink-2 hover:bg-canvas"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-ink-3">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                      {r}
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-3 py-6 text-center text-[12.5px] text-ink-3">{t("globalSearch.hint")}</div>
              )}
            </div>
          )}

          {!loading && query.trim().length >= 2 && flat.length === 0 && (
            <div className="px-3 py-8 text-center text-[13px] text-ink-3">{t("globalSearch.noResults")}</div>
          )}

          {!loading &&
            grouped.map((group) => (
              <div key={group.type}>
                <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-ink-3">
                  {t(`globalSearch.types.${group.type}`)}
                </div>
                {group.items.map((item) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      onClick={() => select(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start",
                        idx === activeIndex ? "bg-primary/10" : "hover:bg-canvas",
                      )}
                    >
                      <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-md", idx === activeIndex ? "bg-primary/15 text-primary" : "bg-canvas text-ink-3")}>
                        {typeIcon[item.type]}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-ink-1">{item.title}</span>
                        {item.subtitle && <span className="block truncate text-[11px] text-ink-3">{item.subtitle}</span>}
                      </span>
                      <span className="shrink-0 rounded-full bg-canvas px-2 py-0.5 text-[9.5px] font-bold text-ink-3">
                        {t(`globalSearch.types.${item.type}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
